'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { Button, Image } from 'react-bootstrap';
import './backButton.scss';

function canUseBrowserBack() {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window.history.length <= 1 || !document.referrer) {
    return false;
  }

  try {
    return new URL(document.referrer).origin === window.location.origin;
  } catch {
    return false;
  }
}

function resolveFallback(pathname = '') {
  if (pathname.startsWith('/product/')) {
    return '/shop';
  }

  if (pathname.startsWith('/hub/')) {
    return '/hub';
  }

  return '/';
}

function shouldHideOnPath(pathname = '', hiddenOnPaths = []) {
  return hiddenOnPaths.includes(pathname);
}

export default function BackButton({
  label = 'Go Back',
  zIndex = 10020,
  forceVisible = false,
  fallback,
  hiddenOnPaths = ['/cart', '/shipping', '/payment', '/placeorder'],
}) {
  const router = useRouter();
  const pathname = usePathname() || '';
  const resolvedFallback = useMemo(
    () => fallback || resolveFallback(pathname),
    [fallback, pathname],
  );
  const [canGoBack, setCanGoBack] = useState(false);
  const [isObscured, setIsObscured] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [isHeroVisible, setIsHeroVisible] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(false);

  useEffect(() => {
    const updateBackAvailability = () => {
      setCanGoBack(canUseBrowserBack());
    };

    updateBackAvailability();
    window.addEventListener('popstate', updateBackAvailability);

    return () => {
      window.removeEventListener('popstate', updateBackAvailability);
    };
  }, [pathname]);

  useEffect(() => {
    const checkBackdrop = () =>
      !!document.querySelector(
        '.modal-backdrop.show, .offcanvas-backdrop.show',
      );

    setIsObscured(checkBackdrop());

    const observer = new MutationObserver(() => {
      setIsObscured(checkBackdrop());
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const footerEl = document.querySelector('footer');

    if (!footerEl) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setIsFooterVisible(entries[0]?.isIntersecting ?? false);
      },
      { threshold: 0.1 },
    );

    observer.observe(footerEl);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let frameId = 0;

    const updateHeroVisibility = () => {
      const heroEl = document.querySelector('.top-products-hero');

      if (!heroEl) {
        setIsHeroVisible(false);
        return;
      }

      const rect = heroEl.getBoundingClientRect();
      setIsHeroVisible(
        rect.bottom > 96 && rect.top < window.innerHeight * 0.78,
      );
    };

    const scheduleHeroVisibilityUpdate = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateHeroVisibility);
    };

    scheduleHeroVisibilityUpdate();

    const observer = new MutationObserver(() => {
      scheduleHeroVisibilityUpdate();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.addEventListener('scroll', scheduleHeroVisibilityUpdate, {
      passive: true,
    });
    window.addEventListener('resize', scheduleHeroVisibilityUpdate);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', scheduleHeroVisibilityUpdate);
      window.removeEventListener('resize', scheduleHeroVisibilityUpdate);
    };
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const doc = document.documentElement;
      const remaining =
        doc.scrollHeight - (window.scrollY + window.innerHeight);
      setIsNearBottom(remaining <= 160);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!forceVisible && shouldHideOnPath(pathname, hiddenOnPaths)) {
    return null;
  }

  return (
    <aside
      className={[
        'go-back-btn-aside',
        isFooterVisible || isNearBottom ? 'hide' : '',
        isHeroVisible ? 'go-back-btn-aside--hero-visible' : '',
        isObscured ? 'go-back-btn-aside--obscured' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      title={label}
      style={{ '--go-back-z': zIndex }}
      aria-hidden={isObscured}
    >
      <Button
        variant="primary"
        className="go-back-btn opacity-80 d-flex align-items-center justify-content-center rounded-circle p-3 shadow-lg"
        onClick={() => {
          if (canUseBrowserBack()) {
            router.back();
            return;
          }

          router.push(resolvedFallback);
        }}
        aria-label={label}
      >
        <Image src="/icons/back.png" alt="" width="22" height="22" />
      </Button>
    </aside>
  );
}
